import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFocusTrap } from '../hooks';
import './TutorialModal.css';

const TutorialModal = ({ onClose }) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);

  // Focus trap for accessibility
  const modalRef = useFocusTrap(true, {
    onEscape: onClose,
    autoFocus: true,
    restoreFocus: true
  });

  // Tutorial steps data
  const steps = [
    {
      id: 'observe',
      icon: 'fas fa-eye',
      title: t('tutorial.step1Title', 'Observe the Image'),
      description: t('tutorial.step1Description', 'You will see a screenshot from an Overwatch map. Look carefully at the details: the environment, signs, structures, and any recognizable landmarks.'),
      image: '/images/tutorial/step1-observe.jpg',
      tip: t('tutorial.step1Tip', 'Pay attention to unique textures, colors, and architectural styles specific to each map.')
    },
    {
      id: 'select',
      icon: 'fas fa-map',
      title: t('tutorial.step2Title', 'Select the Map'),
      description: t('tutorial.step2Description', 'Choose which Overwatch map you think the screenshot is from. Click on the map name to select it and open the map view.'),
      image: '/images/tutorial/step2-select.jpg',
      tip: t('tutorial.step2Tip', 'If unsure, start by eliminating maps that clearly don\'t match the screenshot style.')
    },
    {
      id: 'locate',
      icon: 'fas fa-map-marker-alt',
      title: t('tutorial.step3Title', 'Mark Your Position'),
      description: t('tutorial.step3Description', 'Click on the overhead map to mark where you think the screenshot was taken. You can zoom in/out and drag to navigate the map.'),
      image: '/images/tutorial/step3-locate.jpg',
      tip: t('tutorial.step3Tip', 'Use the zoom controls (+/-) to get a more precise placement.')
    },
    {
      id: 'score',
      icon: 'fas fa-trophy',
      title: t('tutorial.step4Title', 'Earn Points'),
      description: t('tutorial.step4Description', 'Points are awarded based on how close your guess is to the actual location. A perfect guess (within 5 pixels) earns 5000 points!'),
      image: '/images/tutorial/step4-score.jpg',
      tip: t('tutorial.step4Tip', 'Complete all 5 rounds to get your final score and compare with other players on the leaderboard.')
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (index) => {
    setCurrentStep(index);
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  return (
    <div className="tutorial-overlay" role="presentation">
      <div
        className="tutorial-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tutorial-title"
        ref={modalRef}
        tabIndex={-1}
      >
        {/* Header */}
        <div className="tutorial-header">
          <h2 id="tutorial-title">
            <i className="fas fa-graduation-cap"></i>
            {t('tutorial.title', 'How to Play')}
          </h2>
          <button
            className="tutorial-close"
            onClick={onClose}
            aria-label={t('tutorial.close', 'Close tutorial')}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Progress Dots */}
        <div className="tutorial-progress" role="tablist" aria-label={t('tutorial.steps', 'Tutorial steps')}>
          {steps.map((step, index) => (
            <button
              key={step.id}
              className={`progress-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
              onClick={() => handleStepClick(index)}
              aria-label={`${t('tutorial.step', 'Step')} ${index + 1}: ${step.title}`}
              aria-selected={index === currentStep}
              role="tab"
            >
              <span className="dot-number">{index + 1}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="tutorial-content">
          <div className="step-icon">
            <i className={currentStepData.icon}></i>
          </div>

          <h3 className="step-title">{currentStepData.title}</h3>

          <p className="step-description">{currentStepData.description}</p>

          {/* Visual Example */}
          <div className="step-visual">
            <img
              src={currentStepData.image}
              alt={currentStepData.title}
              className="step-image"
              loading="lazy"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="visual-placeholder" style={{ display: 'none' }}>
              <i className={currentStepData.icon}></i>
              <span>{t('tutorial.visualExample', 'Visual Example')}</span>
            </div>
          </div>

          {/* Tip Box */}
          <div className="step-tip">
            <i className="fas fa-lightbulb"></i>
            <span>{currentStepData.tip}</span>
          </div>
        </div>

        {/* Footer Navigation */}
        <div className="tutorial-footer">
          <button
            className="btn btn-tutorial-nav btn-prev"
            onClick={handlePrev}
            disabled={isFirstStep}
          >
            <i className="fas fa-chevron-left"></i>
            <span>{t('tutorial.previous', 'Previous')}</span>
          </button>

          <span className="step-indicator">
            {currentStep + 1} / {steps.length}
          </span>

          <button
            className="btn btn-tutorial-nav btn-next"
            onClick={handleNext}
          >
            <span>{isLastStep ? t('tutorial.finish', 'Got it!') : t('tutorial.next', 'Next')}</span>
            <i className={`fas ${isLastStep ? 'fa-check' : 'fa-chevron-right'}`}></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TutorialModal;
